import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { paymentServiceApi, userServiceApi } from '@/services/api';
import { getMe } from '@/services/authService';
import { analyticsService } from '@/services/analyticsService';
import { toPersianNumber } from '@/utils/persianNumber';

type PaywallMode = 'intro' | 'eligible' | 'ineligible';
type PaywallVariant = 'intro' | 'eligible_without_code' | 'eligible_with_code' | 'ineligible';
type PaywallButtonType = 'renew_subscription' | 'later' | 'never_show_again';

interface SubscriptionPlan {
  plan_id: number;
  plan_name: string;
  price: string;
  discount_price: string | null;
  duration_unit: 'month' | 'day' | 'year';
  duration_value: number;
}

interface GemShopItem {
  item_id: number;
  action: string;
  name: string;
  description: string;
  gems_price: number;
  is_active: boolean;
  discount_code?: string;
  remaining_hours: number | string | null;
  remaining_minutes: number | string | null;
}

interface GemShopResponse {
  user_gems: number;
  items: GemShopItem[];
}

interface SubscriptionStatus {
  is_active?: boolean;
}

interface PurchaseResponse {
  success?: boolean;
  data?: {
    discount_code?: string;
  };
}

const FIXED_DISCOUNT_CODE = 'IBOOKA';
const GEM_SUBSCRIPTION_DISCOUNT_PERCENT = 30;
const DISMISS_KEY = 'booka_paywall_dismissed';
const suppressedForSession = new Set<string>();

const getDismissKey = (mode: PaywallMode) => `${DISMISS_KEY}:${mode}`;

const isDismissed = (mode: PaywallMode) => {
  try {
    return localStorage.getItem(getDismissKey(mode)) === 'true';
  } catch {
    return false;
  }
};

const setDismissed = (mode: PaywallMode) => {
  try {
    localStorage.setItem(getDismissKey(mode), 'true');
  } catch {
    // Ignore storage failures and keep the modal usable.
  }
};

const formatPrice = (value: string | number) => `${Number(value).toLocaleString('fa-IR')} تومان`;

const normalizeItemName = (value: string) => value
  .toLowerCase()
  .replace(/[يى]/g, 'ی')
  .replace(/ك/g, 'ک')
  .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)));

const isTargetSubscriptionDiscountItem = (item: GemShopItem) => {
  const searchableText = normalizeItemName(`${item.name} ${item.description}`);
  const hasThirtyPercent = (searchableText.includes('30') || searchableText.includes('سی'))
    && searchableText.includes('تخفیف');
  const hasThreeMonths = (searchableText.includes('3') || searchableText.includes('سه'))
    && searchableText.includes('ماه');

  return searchableText.includes('اشتراک')
    && hasThirtyPercent
    && hasThreeMonths;
};

const formatRemainingTime = (item: GemShopItem) => {
  const hours = Number(item.remaining_hours || 0);
  const minutes = Number(item.remaining_minutes || 0);

  if (hours > 0 && minutes > 0) return `${toPersianNumber(hours)} ساعت و ${toPersianNumber(minutes)} دقیقه`;
  if (hours > 0) return `${toPersianNumber(hours)} ساعت`;
  return `${toPersianNumber(minutes)} دقیقه`;
};

const isThreeMonthPlan = (plan: SubscriptionPlan) => (
  (plan.duration_unit === 'month' && Number(plan.duration_value) === 3)
  || (plan.duration_unit === 'day' && Number(plan.duration_value) >= 89 && Number(plan.duration_value) <= 93)
);

const SubscriptionPlanBox = ({ plan }: { plan: SubscriptionPlan }) => {
  const hasDiscount = plan.discount_price !== null;
  const originalPrice = Number(plan.price);
  const finalPrice = Number(hasDiscount ? plan.discount_price : plan.price);
  const discountPercent = hasDiscount && originalPrice > 0
    ? Math.round((1 - finalPrice / originalPrice) * 100)
    : 0;

  return (
    <div className="relative rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_0_10px_-2px_rgba(0,0,0,0.14)]" dir="rtl">
      {discountPercent > 0 && (
        <div className="absolute -top-2 left-3 rounded-lg bg-red-500 px-2 py-1 text-[9px] font-bold text-white">
          {toPersianNumber(discountPercent)}٪ تخفیف
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-base font-bold text-gray-800">{plan.plan_name}</h3>
        <div className="text-left">
          <div className="text-lg font-extrabold text-black">{formatPrice(finalPrice)}</div>
          {hasDiscount && (
            <div className="text-xs text-gray-400 line-through">{formatPrice(originalPrice)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

const SubscriptionPaywallModal = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<PaywallMode | null>(null);
  const [name, setName] = useState('دوست من');
  const [gemItem, setGemItem] = useState<GemShopItem | null>(null);
  const [plan, setPlan] = useState<SubscriptionPlan | null>(null);
  const [discountCode, setDiscountCode] = useState<string | null>(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const wasPaywallVisible = useRef(false);

  const loadPaywall = async () => {
    try {
      const [profile, statusResponse, shopResponse, plansResponse] = await Promise.all([
        getMe(),
        paymentServiceApi.get<SubscriptionStatus>('/subscriptions/status'),
        userServiceApi.get<GemShopResponse>('/gem-shop/items'),
        paymentServiceApi.get<SubscriptionPlan[]>('/subscription-plans/'),
      ]);

      if (statusResponse.data?.is_active || !shopResponse.data || !plansResponse.data) return;

      if (suppressedForSession.size > 0) return;

      const subscriptionDiscountItem = shopResponse.data.items.find(
        isTargetSubscriptionDiscountItem,
      ) || null;
      const threeMonthPlan = plansResponse.data.find(isThreeMonthPlan) || null;

      if (!subscriptionDiscountItem || !threeMonthPlan) return;

      const existingCode = subscriptionDiscountItem.is_active
        ? subscriptionDiscountItem.discount_code || null
        : null;
      // An already-issued code remains the user's available offer even after
      // the gems used to obtain it have been deducted.
      const hasEnoughGems = Boolean(existingCode)
        || shopResponse.data.user_gems >= subscriptionDiscountItem.gems_price;
      let availableMode: PaywallMode | null = null;
      if (!isDismissed('intro')) {
        availableMode = 'intro';
      } else if (hasEnoughGems) {
        if (!isDismissed('eligible')) {
          availableMode = 'eligible';
        } else if (existingCode && !isDismissed('ineligible')) {
          // An existing code can still fall back to mode 2 when mode 1
          // has already been dismissed.
          availableMode = 'ineligible';
        }
      } else if (!isDismissed('ineligible')) {
        availableMode = 'ineligible';
      }

      setName(profile.name || 'دوست من');
      setGemItem(subscriptionDiscountItem);
      setPlan(threeMonthPlan);
      setDiscountCode(existingCode);
      setMode(availableMode);
    } catch (error) {
      console.warn('[SubscriptionPaywallModal] Failed to load paywall data', error);
    }
  };

  useEffect(() => {
    void loadPaywall();
  }, []);

  const paywallVariant: PaywallVariant | null = mode === 'eligible'
    ? (discountCode ? 'eligible_with_code' : 'eligible_without_code')
    : mode;

  useEffect(() => {
    const isPaywallVisible = Boolean(paywallVariant && plan && gemItem);

    if (isPaywallVisible && !wasPaywallVisible.current) {
      void analyticsService.logEvent('view_paywall', {
        paywall_mode: paywallVariant,
      });
    }

    wasPaywallVisible.current = isPaywallVisible;
  }, [gemItem, paywallVariant, plan]);

  const trackButtonClick = (buttonType: PaywallButtonType) => {
    if (!paywallVariant) return;

    void analyticsService.logEvent('click_paywall', {
      paywall_mode: paywallVariant,
      button_type: buttonType,
    });
  };

  const suppressForCurrentSession = () => {
    suppressedForSession.add('paywall');
    setMode(null);
  };

  const close = () => {
    trackButtonClick('later');
    suppressForCurrentSession();
  };

  const handleDismiss = () => {
    if (!mode) return;
    trackButtonClick('never_show_again');
    setDismissed(mode);
    suppressForCurrentSession();
  };

  const purchaseDiscount = async () => {
    if (!gemItem || isPurchasing) return;
    setIsPurchasing(true);
    try {
      const response = await userServiceApi.post<PurchaseResponse>(`/gem-shop/purchase/${gemItem.item_id}`);
      const code = response.data?.data?.discount_code;
      if (response.success && code) {
        setDiscountCode(code);
        return code;
      }
    } finally {
      setIsPurchasing(false);
    }
    return null;
  };

  const handleRenew = async () => {
    if (!plan || !mode) return;
    trackButtonClick('renew_subscription');
    let code = mode === 'ineligible' ? FIXED_DISCOUNT_CODE : discountCode;

    if (mode === 'eligible' && !code) {
      code = await purchaseDiscount();
      if (!code) return;
    }

    suppressForCurrentSession();
    navigate('/payment-confirmation', {
      state: {
        autoSelectSubscriptionPlan: {
          duration_unit: plan.duration_unit,
          duration_value: plan.duration_value,
        },
        ...(code ? { autoDiscountCode: code } : {}),
      },
    });
  };

  if (!mode || !plan || !gemItem) return null;

  const showDiscountFeature = mode === 'eligible';
  const hasEligibleCode = showDiscountFeature && !!discountCode;
  const eligibleDiscountedPlan = showDiscountFeature
    ? {
      ...plan,
      discount_price: String(
        Number(plan.discount_price ?? plan.price)
        * (1 - GEM_SUBSCRIPTION_DISCOUNT_PERCENT / 100),
      ),
    }
    : plan;
  const eligibleDiscountPercent = showDiscountFeature && Number(plan.price) > 0
    ? Math.round((1 - Number(eligibleDiscountedPlan.discount_price) / Number(plan.price)) * 100)
    : GEM_SUBSCRIPTION_DISCOUNT_PERCENT;
  const fixedCodePlan = mode === 'ineligible'
    ? {
      ...plan,
      discount_price: String(Number(plan.discount_price ?? plan.price) * 0.7),
    }
    : plan;
  const fixedCodeDiscountPercent = mode === 'ineligible' && Number(plan.price) > 0
    ? Math.round((1 - Number(fixedCodePlan.discount_price) / Number(plan.price)) * 100)
    : GEM_SUBSCRIPTION_DISCOUNT_PERCENT;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4" dir="rtl">
      <div className="max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto rounded-3xl bg-[#FFFDFC] px-6 py-3 shadow-2xl" role="dialog" aria-modal="true">
        <img src="/pay_wall_icon.webp" alt="" className="mx-auto mb-2 h-56 w-56 object-contain" />
        <h2 className="text-center text-base font-bold text-gray-900">{name} اشتراک نداری !</h2>
        {showDiscountFeature ? (
          hasEligibleCode ? (
            <p className="mt-2 text-center text-xs leading-6 text-gray-700">همین الان اشتراک بوکا رو با تخفیف {toPersianNumber(eligibleDiscountPercent)} درصدی بخر تا به همه کتاب ها و قابلیت هاشون دسترسی داشته باشی 🔥</p>
          ) : (
            <p className="mt-2 text-center text-xs leading-6 text-gray-700">
              همین الان با {toPersianNumber(gemItem.gems_price)} الماس کد تخفیف {toPersianNumber(eligibleDiscountPercent)} درصدی رو دریافت کن و اشتراک بوکا رو بخر تا به همه کتاب ها و قابلیت هاشون دسترسی داشته باشی 🔥
            </p>
          )
        ) : mode === 'ineligible' ? (
          <p className="mt-2 text-center text-xs leading-6 text-gray-700">همین الان اشتراک بوکا رو با تخفیف {toPersianNumber(fixedCodeDiscountPercent)} درصدی بخر تا به همه کتاب ها و قابلیت هاشون دسترسی داشته باشی 🔥</p>
        ) : (
          <p className="mt-2 text-center text-xs leading-6 text-gray-700">همین الان اشتراک بوکا رو با تخفیف بخر تا به همه کتاب ها و قابلیت هاشون دسترسی داشته باشی 🔥</p>
        )}

        {mode === 'intro' && <div className="mt-4"><SubscriptionPlanBox plan={plan} /></div>}

        {showDiscountFeature && (
          <div className="mt-4">
            <SubscriptionPlanBox plan={eligibleDiscountedPlan} />
            <p className="mt-2 text-center text-[10px] font-medium leading-relaxed text-gray-500">
              {hasEligibleCode
                ? `فقط ${formatRemainingTime(gemItem)} مونده تا این تخفیف رو از دست بدی !`
                : 'این کد تخفیف فقط ۲۴ ساعت اعتبار داره !'}
            </p>
          </div>
        )}

        {mode === 'ineligible' && (
          <div className="mt-4">
            <SubscriptionPlanBox plan={fixedCodePlan} />
          </div>
        )}

        <button type="button" onClick={handleRenew} disabled={isPurchasing} className="mt-5 w-full rounded-2xl bg-[#7063FF] px-4 py-3.5 text-sm font-bold text-white disabled:opacity-60">
          {isPurchasing && showDiscountFeature && !hasEligibleCode ? (
            <Loader2 className="mx-auto h-5 w-5 animate-spin" aria-label="در حال دریافت کد تخفیف" />
          ) : (
            showDiscountFeature && !hasEligibleCode ? 'دریافت کد تخفیف و خرید اشتراک' : 'خرید اشتراک'
          )}
        </button>
        <div className="mt-3 flex gap-3">
          <button type="button" onClick={close} className="flex-[1.5] rounded-xl border border-gray-300 bg-gray-300 py-2 text-sm font-bold text-gray-800">بعدا</button>
          <button type="button" onClick={handleDismiss} className="flex-1 rounded-xl border border-gray-400 py-2 text-sm font-bold text-gray-700">دیگه نمایش نده</button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaywallModal;
